/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import dayjs from 'dayjs';
import {
  useDataProvider,
  DatePicker,
  FieldWrapperVertical,
  TimePicker,
  useMasterData,
  SnackbarApiErrorMessage,
  useLocalization,
  useSnackbar,
  LoadingEvent,
} from '@ui-framework/index';
import { useNavigate } from 'react-router-dom';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { IconLibrary } from '@product-framework/index';
import { nanoid } from 'nanoid';
import formatDateTime from '@product-framework/utils/formatDateTime';
import DialogCloseButton from './DialogCloseButton';

function AssignJobDialog({ open, onClose, jobInfo, selectedServiceProvider }) {
  const navigate = useNavigate();
  const {
    id: jobId,
    serviceProviderId: providerForCurrentJob,
    serviceJobStatus,
    customerServiceRequests = [],
  } = jobInfo;
  const { sp, spAddress } = selectedServiceProvider;
  const { id: serviceProviderId, name: serviceProviderName } = sp;
  const { locationId } = spAddress;
  const { dispatchEvent } = sp;
  const acceptedLocationId = locationId;

  const { formatMessage } = useLocalization();
  const { pendingServiceJobStatus } = useMasterData();
  const canAssign = pendingServiceJobStatus.includes(serviceJobStatus);

  const snackbar = useSnackbar();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, post, get } = useDataProvider(provider);

  const getDispatchMethodStatus = async spId => {
    const url = `${getApiUrl()}/sxcl/v1/service-provider-profile/${spId}/dispatch`;
    const response = await get(url);
    if (response?.ok) {
      return response?.data?.dispatchMethod;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const getServiceProvider = async spId => {
    const url = `${getApiUrl()}/sxcl/v1/service-providers/${spId}`;
    const response = await get(url);
    if (response?.ok) {
      return response.data;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const countOfCSR = Array.isArray(customerServiceRequests)
    ? customerServiceRequests?.length
    : 0;

  const dateOptions =
    customerServiceRequests?.map((date, idx) => ({
      label: formatDateTime(
        date?.requestDate,
        date?.afterHour,
        date?.beforeHour,
      ),
      value: `csr_${idx}`,
    })) || [];

  const assign =
    providerForCurrentJob === serviceProviderId || !providerForCurrentJob;

  let schema;

  if (countOfCSR === 0) {
    schema = yup.object().shape({
      appointmentDate: yup
        .string()
        .required(
          formatMessage({
            id: 'APPOINTMENT_DATE_REQUIRED',
            defaultMessage: 'Date is required',
          }),
        )
        .test(
          'not-in-past',
          formatMessage({
            id: 'APPOINTMENT_DATE_PAST',
            defaultMessage: 'Date cannot be in the past',
          }),
          value => !dayjs(value).isBefore(dayjs(), 'day'),
        ),
    });
  } else {
    schema = yup.object().shape({
      selectedDateOption: yup.string().required(
        formatMessage({
          id: 'CSR_DATE_REQUIRED',
          defaultMessage: 'Please select a date option',
        }),
      ),
      appointmentDate: yup.string().when('selectedDateOption', {
        is: 'custom',
        then: s =>
          s
            .required(
              formatMessage({
                id: 'APPOINTMENT_DATE_REQUIRED',
                defaultMessage: 'Date is required',
              }),
            )
            .test(
              'not-in-past',
              formatMessage({
                id: 'APPOINTMENT_DATE_PAST',
                defaultMessage: 'Date cannot be in the past',
              }),
              value => !dayjs(value).isBefore(dayjs(), 'day'),
            ),
        otherwise: s => s.notRequired(),
      }),
    });
  }

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      selectedDateOption: countOfCSR === 0 ? 'custom' : 'csr_0',
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    reset,
  } = methods;

  const selectedDateOption = watch('selectedDateOption');
  const showCustomInputs = countOfCSR === 0 || selectedDateOption === 'custom';

  const [view, setView] = useState('MAIN');
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [dispatchMethod, setDispatchMethod] = useState('');
  const [currentServiceProvider, setCurrentServiceProvider] = useState({});

  async function onFormSubmit(data) {
    setLoading(true);

    let body;

    if (countOfCSR === 0 || data.selectedDateOption === 'custom') {
      body = {
        serviceDate: data.appointmentDate,
        serviceDateAfterHour: data.serviceDateAfterHour,
        serviceDateBeforeHour: data.serviceDateBeforeHour,
        jobRecordVersion: jobInfo.recordVersion,
        dispatchEventId: dispatchEvent?.id,
      };
    } else {
      const idx = parseInt(data.selectedDateOption.replace('csr_', ''), 10);
      const csr = customerServiceRequests[idx];
      body = {
        serviceDate: csr.requestDate,
        serviceDateAfterHour: csr.afterHour,
        serviceDateBeforeHour: csr.beforeHour,
        jobRecordVersion: jobInfo.recordVersion,
        dispatchEventId: dispatchEvent?.id,
      };
    }

    const queryParams = new URLSearchParams({
      reassignFlag: !assign,
    });

    if (acceptedLocationId != null && acceptedLocationId !== '') {
      queryParams.set('locationId', String(acceptedLocationId));
    }

    const response = await post(
      `${getApiUrl()}/sxcl/v1/service-providers/${serviceProviderId}/pending-acceptance/${jobId}/assign?${queryParams.toString()}`,
      body,
    );
    if (response.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'ASSIGN_JOB_SUCCESS',
          defaultMessage: 'Job was assigned successfully',
        }),
      );
      onClose(nanoid());
    } else {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={response?.errors} />,
      });
    }
    setLoading(false);
  }

  const buttons = [
    {
      label: formatMessage({ id: 'YES', defaultMessage: 'Yes' }),
      onClick: () => setView(assign ? 'ASSIGN' : 'REASSIGN'),
      variant: 'contained',
      color: 'primary',
      disabled: !canAssign,
      show: ['MAIN'],
      startIcon: <IconLibrary.Yes />,
    },
    {
      label: formatMessage({ id: 'ASSIGN', defaultMessage: 'Assign' }),
      type: 'submit',
      variant: 'contained',
      color: 'primary',
      disabled: !canAssign,
      show: ['ASSIGN'],
      startIcon: <IconLibrary.Yes />,
    },
    {
      label: formatMessage({ id: 'REASSIGN', defaultMessage: 'Reassign' }),
      type: 'submit',
      variant: 'contained',
      color: 'primary',
      disabled: !canAssign,
      show: ['REASSIGN'],
      startIcon: <IconLibrary.Yes />,
    },
    {
      label: formatMessage({ id: 'CANCEL', defaultMessage: 'Cancel' }),
      onClick: onClose,
      variant: 'contained',
      color: 'secondary',
      startIcon: <IconLibrary.Cancel />,
    },
  ];

  const initialize = async () => {
    setInitialLoading(true);

    if (open) {
      const dispatchMethodResponse = await getDispatchMethodStatus(sp?.id);
      const spResponse = await getServiceProvider(sp?.id);
      setCurrentServiceProvider(spResponse);
      setDispatchMethod(dispatchMethodResponse);
      setView('MAIN');
      reset({
        selectedDateOption: countOfCSR === 0 ? 'custom' : 'csr_0',
        appointmentDate: undefined,
        serviceDateAfterHour: undefined,
        serviceDateBeforeHour: undefined,
      });
    }
    setInitialLoading(false);
  };

  useEffect(() => {
    initialize();
  }, [open, jobId, countOfCSR, reset]);

  const activeServiceProvider = currentServiceProvider?.status === 'ACTIVE';
  const isAssignable = activeServiceProvider && dispatchMethod !== 'INACTIVE';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconLibrary.AssignServiceJob />
        {formatMessage({ id: 'ASSIGN_JOB', defaultMessage: 'Assign Job' })}
        &nbsp;&ndash;
        <Typography color="primary" variant="body">
          {jobInfo?.serviceJobNumber}
        </Typography>
        <DialogCloseButton disabled={loading} onClose={onClose} />
      </DialogTitle>
      {initialLoading ? (
        <Box sx={{ height: '100px' }}>
          <LoadingEvent>Loading...</LoadingEvent>
        </Box>
      ) : !isAssignable ? (
        <Box>
          <DialogContent>
            <Typography sx={{ mb: 0 }}>
              This Service Provider is inactive and cannot be assigned to a Job.
            </Typography>
            <Typography>
              To proceed, update the Service Provider&apos;s profile{' '}
            </Typography>
            <ul>
              {sp?.status === 'INACTIVE' && (
                <li>
                  <Typography sx={{ fontWeight: '500' }} variant="body1">
                    Service Provider Status must be Active.{' '}
                    <Typography
                      color="error"
                      component="span"
                      sx={{ fontWeight: '500' }}
                      variant="caption">
                      (Currently {sp?.status})
                    </Typography>
                  </Typography>
                </li>
              )}
              {dispatchMethod === 'INACTIVE' && (
                <li>
                  <Typography sx={{ fontWeight: '500' }} variant="body1">
                    Dispatch Method cannot be Inactive.{' '}
                    <Typography
                      color="error"
                      component="span"
                      sx={{ fontWeight: '500' }}
                      variant="caption">
                      (Currently {dispatchMethod})
                    </Typography>
                  </Typography>
                </li>
              )}
            </ul>
          </DialogContent>
          <DialogActions
            display="flex"
            sx={{ justifyContent: 'center', my: 2 }}>
            <Button
              startIcon={<IconLibrary.ServiceProvider />}
              onClick={() => {
                navigate(`/serviceproviders/edit/${sp.id}`);
              }}
              variant="contained">
              Open Service Provider
            </Button>
            <Button
              startIcon={<IconLibrary.Close />}
              variant="outlined"
              onClick={() => onClose()}>
              Close
            </Button>
          </DialogActions>
        </Box>
      ) : (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onFormSubmit)}>
            <DialogContent>
              {loading ? (
                <Box textAlign="center">
                  <CircularProgress size="30px" />
                  <Typography>Processing</Typography>
                </Box>
              ) : (
                <>
                  {view === 'MAIN' && (
                    <Typography>
                      Please confirm you would like to{' '}
                      {assign ? 'assign' : 'reassign'} this Job to{' '}
                      {serviceProviderName}
                    </Typography>
                  )}
                  {(view === 'ASSIGN' || view === 'REASSIGN') && (
                    <Box>
                      {countOfCSR >= 1 && (
                        <Controller
                          name="selectedDateOption"
                          control={control}
                          render={({ field }) => (
                            <FormControl
                              error={!!errors.selectedDateOption}
                              sx={{ width: '100%' }}>
                              <RadioGroup {...field}>
                                {dateOptions.map(opt => (
                                  <FormControlLabel
                                    key={opt.value}
                                    value={opt.value}
                                    control={<Radio />}
                                    label={opt.label}
                                  />
                                ))}
                                <FormControlLabel
                                  value="custom"
                                  control={<Radio />}
                                  label={formatMessage({
                                    id: 'CUSTOM_DATE_TIME',
                                    defaultMessage: 'Other',
                                  })}
                                />
                              </RadioGroup>
                              {errors.selectedDateOption && (
                                <FormHelperText>
                                  {errors.selectedDateOption.message}
                                </FormHelperText>
                              )}
                            </FormControl>
                          )}
                        />
                      )}
                      {showCustomInputs && (
                        <Grid2 container spacing={1} sx={{ mt: 2 }}>
                          <Grid2 xs={4}>
                            <FieldWrapperVertical
                              field={{
                                name: 'appointmentDate',
                                label: {
                                  localeId: 'appointmentDate',
                                  defaultMessage: 'Appointment Date',
                                },
                              }}
                              errors={errors}>
                              <DatePicker disablePast name="appointmentDate" />
                            </FieldWrapperVertical>
                          </Grid2>
                          <Grid2 xs={3}>
                            <Controller
                              name="serviceDateAfterHour"
                              control={control}
                              render={({ field }) => (
                                <FieldWrapperVertical
                                  field={{
                                    name: 'serviceDateAfterHour',
                                    label: {
                                      localeId: 'SERVICE_DATE_AFTER_HOUR_LABEL',
                                      defaultMessage: 'After',
                                    },
                                  }}>
                                  <TimePicker {...field} />
                                </FieldWrapperVertical>
                              )}
                            />
                          </Grid2>
                          <Grid2 xs={3}>
                            <Controller
                              name="serviceDateBeforeHour"
                              control={control}
                              render={({ field }) => (
                                <FieldWrapperVertical
                                  field={{
                                    name: 'serviceDateBeforeHour',
                                    label: {
                                      localeId:
                                        'SERVICE_DATE_BEFORE_HOUR_LABEL',
                                      defaultMessage: 'Before',
                                    },
                                  }}>
                                  <TimePicker {...field} />
                                </FieldWrapperVertical>
                              )}
                            />
                          </Grid2>
                        </Grid2>
                      )}
                    </Box>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', my: 2 }}>
              {buttons
                .filter(btn => !btn.show || btn.show.includes(view))
                .map(({ label, ...btn }) => (
                  <Button key={label} disabled={loading} {...btn}>
                    {label}
                  </Button>
                ))}
            </DialogActions>
          </form>
        </FormProvider>
      )}
    </Dialog>
  );
}

export default AssignJobDialog;
